import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserTeamAccess } from '../config/queries'
import { useReactivateGuild } from '../config/mutations'

type ActionableEntity = {
  ids: string[]
  teamReference?: wowaudit.TeamReference
  text: string
  ctaPath?: string
  onCtaClick?: () => void
  ctaText: string
  ctaLoading?: boolean
  ctaDisabled?: boolean
  dismissible?: boolean
  className?: string
}

export const useActionable = (user?: wowaudit.User) => {
  const { t } = useTranslation()
  const reactivateGuild = useReactivateGuild()

  const storedDismissedActions = JSON.parse(
    localStorage.getItem('dismissedActions') ?? '[]',
  )
  const [dismissedActions, setDismissedActions] = useState<string[]>(
    storedDismissedActions,
  )

  const dismissActionableEntity = (entity: ActionableEntity) => {
    const newDismissedActions = [...dismissedActions, ...entity.ids]
    setDismissedActions(newDismissedActions)
    localStorage.setItem(
      'dismissedActions',
      JSON.stringify(newDismissedActions),
    )
  }

  const actionableCharacters =
    user?.characterReferences?.filter(
      (ch) =>
        ch.actionable &&
        !dismissedActions.includes(`character_${ch.id}_${ch.gdprStatus}`),
    ) ?? []

  const actionableTeams =
    user?.teamReferences?.filter(
      (t) =>
        t.markedForDeletionAt &&
        !dismissedActions.includes(`team_${t.id}_${t.markedForDeletionAt}`),
    ) ?? []

  const actionableGuilds = Object.values(
    (user?.teamReferences ?? []).reduce((guilds, team) => {
      const guild = team.ownerReference

      if (
        guild.active === false &&
        window.location.pathname.startsWith(guild.path)
      ) {
        guilds[guild.id] = guild
      }

      return guilds
    }, {} as Record<string, wowaudit.GuildReference>),
  )

  const { data: accessForTeams } = useUserTeamAccess(
    actionableTeams?.map((t) => t.id) ?? [],
  )

  const actionableTeamsWithAccess = actionableTeams.filter(
    (t) => accessForTeams?.[t.id.toString()],
  )

  const actionableEntities: ActionableEntity[] = [
    ...actionableGuilds.map((entity) => ({
      ids: [`guild_${entity.id}_inactive`],
      text: t('profile.guild-inactive-warning', {
        name: entity.name,
      }),
      onCtaClick: () => reactivateGuild.mutate(entity),
      ctaText: t('profile.reactivate-guild'),
      ctaLoading:
        reactivateGuild.isPending &&
        reactivateGuild.variables?.id === entity.id,
      ctaDisabled: reactivateGuild.isPending,
      dismissible: false,
      className: 'bg-warning-foreground',
    })),
    ...actionableTeamsWithAccess.map((entity) => ({
      ids: [`team_${entity.id}_${entity.markedForDeletionAt}`],
      teamReference: entity,
      text: t('profile.team-requires-action-warning', {
        name: entity.name,
        owner: entity.ownerReference.name,
      }),
      ctaPath:
        window.location.pathname === `${entity.path}/settings#team`
          ? undefined
          : `${entity.path}/settings#team`,
      ctaText: t('profile.team-settings'),
      className: 'bg-destructive-foreground',
    })),
    ...(actionableCharacters.length
      ? [
          {
            ids: actionableCharacters.map(
              (ch) => `character_${ch.id}_${ch.gdprStatus}`,
            ),
            text: t('profile.character-requires-action-warning'),
            ctaPath:
              window.location.pathname === '/profile/characters'
                ? undefined
                : '/profile/characters',
            ctaText: t('header.profile'),
            className: 'bg-warning-foreground',
          },
        ]
      : []),
  ]

  const [currentActionIndex, setCurrentActionIndex] = useState<number>(0)
  const currentAction: ActionableEntity | undefined =
    actionableEntities[currentActionIndex]

  const hasNextAction = currentActionIndex < actionableEntities.length - 1
  const hasPreviousAction = currentActionIndex > 0

  return {
    actionableEntities,
    currentAction,
    currentActionIndex,
    setCurrentActionIndex,
    hasNextAction,
    hasPreviousAction,
    dismissActionableEntity,
  }
}
